import { type Update } from './base-ui/CoreUpdate';
import type { IDiagnosisModel } from './components/DxSummary/models/DiagnosisModel/DiagnosisModel.types';
import { type NotificationType } from './contexts/Notification.context';
import type { PushContext } from './modules/push2emr/types';
import { type DashboardNamesType } from './pages/analytics/reporting/Types';
import type { TabId } from './pages/summary/Tabs';
import type { AnnotationLineGroup } from './pages/summary/med-features/lab-trend-view/lab-graph/annotations-types';
import type {
	Diagnosis as PushToEmrDiagnosis,
	HCCs,
	SourceData,
	SourceType,
	SourceType as PushToEmrDiagnosisSourceType,
	Action,
	Diagnosis,
} from './pages/summary/med-features/push-to-emr/types';
import type { IcdDataDBSchema } from './schemas';
import type { SurgicalHistoryEntry } from './server/PatientPortraitApi/PatientPortraitApi';
import type {
	DiagnosisAction,
	SuggestionOrigin,
	RafData,
	HccVersions,
	GroupRiskModel,
	DiagnosisStatus,
	ClientDiagnosisCode,
} from '@dxcapture/core';
import type { ElectronicMedicalRecord, NID, SID } from '@navina/api-types';
import { type ArgsProps } from 'antd/es/notification';
import type { z } from 'zod';

export type AddonButtonClickBehaviorTypes = 'tab' | 'window';
export type NoteSectionType = 'HPI' | 'PE' | 'PI';

interface ProviderName {
	readonly lastName: string;
	readonly firstName: string;
}

interface PreviousEncounter {
	readonly date: string;
	readonly reasonForVisit: string | ReadonlyArray<string>; // This one might be an empty object as well
	readonly providers: ReadonlyArray<ProviderName>;
}

interface ProblemGraphDetails {
	readonly date: string;
	readonly observationValue?: string | number;
	readonly isNormal?: boolean;
}

export interface ProblemGraphData {
	readonly title: string;
	readonly data: ProblemGraphDetails;
}

interface FileData {
	readonly path: string;
	readonly numOfPages: number;
	readonly name: string;
	readonly date: string;
	readonly bottomLine: string;
}

export interface ProblemVitalsType {
	observationValue: string; // TODO - make readonly
	readonly date: string;
	readonly title: string;
}

export interface ProblemDocList {
	readonly [name: string]: Array<ProblemDocType>; // TODO - make ReadonlyArray
}

export interface ProblemType {
	readonly title: string;
	readonly indicateSmoker: boolean;
	readonly indicateAlcoholism: boolean;
	readonly icd: string;
	readonly snomed: string;
	readonly medications?: ReadonlyArray<string>;
	readonly medsExacerbate?: ReadonlyArray<string>;
	readonly immunizationsReccomendations?: ReadonlyArray<string>;
	readonly vitals?: Array<ProblemVitalsType>; // TODO - make ReadonlyArray
	readonly files?: ReadonlyArray<FileData>;
	readonly other?: ReadonlyArray<string>;
	readonly graphsData: ReadonlyArray<ProblemGraphData>;
	readonly docs?: ProblemDocList;
	readonly groupedProblems?: ReadonlyArray<{ readonly name: string; readonly snomedCode: string }>;
}

interface IcdDataType {
	readonly code: string;
	readonly hccWeight: number;
	readonly hccGroup?: string;
	readonly description: string;
}

interface EncountersMissingDiagnosesType {
	readonly data: { readonly encounterDate: string; readonly encounterId: number };
	readonly snomedCode: string;
	readonly snomedName: string;
	readonly icdCode: ReadonlyArray<string>;
	readonly icdData: ReadonlyArray<IcdDataType>;
	readonly rankScore: number;
}

interface ObservationsMissingDiagnosesType {
	readonly icdCode: ReadonlyArray<string>;
	readonly snomed: { readonly code: string; readonly description: string };
	readonly icdData: ReadonlyArray<IcdDataType>;
	readonly data: unknown;
}

interface MsoRecommendationBaseType {
	readonly suggestionType: 'Suspect' | 'Chronic';
	readonly providerFirstName: string;
	readonly providerLastName: string;
	readonly date: string;
	readonly memo: string;
	readonly msoName?: string;
}

export interface AgilonNonDxType extends MsoRecommendationBaseType {
	readonly navinaSuggestionsId: string;
	readonly agilonSuggestionId: string | null;
	readonly code: string;
	readonly lastModified: string;
}

export type AgilonNonDxUserSelectionType = 'agree' | 'disagree' | 'ignore';

export interface AgilonNonDxStatusDataType {
	readonly agilonSuggestionId: string | null;
	readonly createdAt: string;
	readonly navinaSuggestionId: string;
	status: AgilonNonDxUserSelectionType; // TODO - make readonly
	updatedAt: string; // TODO - make readonly
	readonly updatedBy?: string;
}

interface ConsultMissingDiagnosesType {
	readonly fileId: string;
	readonly fileName: string;
	readonly fileDate: string;
	readonly fileLoinc: string;
	readonly fileLoincName: string;
	readonly fileLabel: string;
	readonly lineContentOccuranceInDoc: number;
	readonly icd: string;
	readonly snomedCode: string;
	readonly description: string;
	readonly modelScore: number;
	readonly icd10CodeLineContent: string;
}

interface RafGapType {
	readonly icd_code: ReadonlyArray<string>;
	readonly snomed_code?: string;
	readonly data: { readonly modificationDate: string };
	readonly icdData: ReadonlyArray<IcdDataType>;
	readonly snomedCode: string;
	readonly operators?: ReadonlyArray<InferredPmdLabOperatorType>;
	readonly hccGroup: string;
	readonly hccWeight: number;
	readonly billable: boolean;
	readonly billableDescendants: ReadonlyArray<BillableDescendant>;
}

interface InferredPmdLabOperatorDataType {
	readonly value: string;
	readonly operator: string;
	readonly date: string;
	readonly source: string;
	readonly fileId?: string;
	readonly fileName?: string;
}

interface InferredPmdMedicationOperatorDataType {
	readonly drugName: string;
	readonly dosage: string;
	readonly operator: string;
	readonly date: string;
	readonly source: string;
}

interface InferredPmdLabOperatorType {
	readonly name: string;
	readonly result: boolean;
	readonly data: ReadonlyArray<InferredPmdLabOperatorDataType>;
}

interface InferredPmdMedicationOperatorType {
	readonly name: string;
	readonly result: boolean;
	readonly data: ReadonlyArray<InferredPmdMedicationOperatorDataType>;
}

interface WithDescription {
	readonly description: string;
}

export interface SnomedCodeAndDescription extends WithDescription {
	readonly snomedCode: string;
}

interface CodeAndDescription extends WithDescription {
	readonly code: string;
}

interface InferredMissingDiagnosesType {
	readonly operators: ReadonlyArray<InferredPmdLabOperatorType | InferredPmdMedicationOperatorType>;
	readonly icdCode: string;
	readonly hccWeight: number;
	readonly icdData: ReadonlyArray<IcdDataType>;
	readonly plInclusion?: ReadonlyArray<SnomedCodeAndDescription>;
}

export interface NoteData {
	readonly text: string;
	readonly authorId?: string;
	readonly authorName: string;
	readonly authorType: 'provider' | 'coder';
	shouldPush: boolean; // TODO - make readonly
	readonly noteDate: string;
	readonly insertionDate: unknown;
	readonly addedInPostVisit?: boolean;
	readonly noteId?: string | null;
}

export interface IcdNotes {
	readonly icdCode: string;
	readonly noteData: NoteData;
}

export interface DxFromEmr {
	readonly icd10: CodeAndDescription;
	readonly snomed: CodeAndDescription | null;
}

interface ConsolidatedMissingDiagnosesType {
	readonly icdCode: string;
	readonly icdDescription: string;
	readonly hccWeight: number;
	readonly hccGroup: string;
	readonly snomedDescription: string;
	readonly reasons: {
		readonly consult?: ConsultMissingDiagnosesType;
		readonly inferred?: ObservationsMissingDiagnosesType | InferredMissingDiagnosesType;
		readonly encounter?: EncountersMissingDiagnosesType;
	};
	readonly icdData: ReadonlyArray<DocIcdData>;
	readonly sourceData?: SourceData;
	readonly date?: Date;
	readonly billable?: boolean;
	readonly billableDescendants?: ReadonlyArray<BillableDescendant>;
}

export interface RecommendedDiagnosis {
	readonly icdCode: string;
	readonly icdDescription: string;
	readonly hccWeight: number;
	readonly hccGroup: string;
	readonly snomedCode: string;
	readonly snomedDescription: string;
	readonly sourceData?: SourceData;
	readonly diagnosisSources: Array<DiagnosisSources>; // TODO - make ReadonlyArray
	readonly icdData: ReadonlyArray<DocIcdData>;
	readonly date: Date;
	readonly billable?: boolean;
	readonly billableDescendants?: ReadonlyArray<BillableDescendant>;
	readonly isPrimary: boolean;
	readonly addressedGroup?: DiagnosisAddressedGroup;
	readonly noteData?: NoteData;
	readonly companionOrder: number;
	readonly primaryDx: string;
	readonly companionDxs: ReadonlyArray<string>;
	readonly isNavinaCoupledSuggestion: boolean;
	readonly isCoupledSuggestion: boolean;
	readonly companionAddressedGroup: DiagnosisAddressedGroup;
	readonly dxId: string;
	readonly addedInPostVisit?: boolean;
}

interface SuggestedDiagnoses {
	readonly dxSuggestions: ReadonlyArray<RecommendedDiagnosis>;
	readonly isValid: boolean;
	readonly actionsHistory?: ReadonlyArray<any>;
}

// TODO - change to enum
export type DiagnosisSourceType =
	| 'pl_gap'
	| 'raf_gap'
	| 'imaging_inferred'
	| 'hie_encounters'
	| 'external_suggestion'
	| 'ner_from_consults'
	| 'claims'
	| 'claim_cms'
	| 'consults'
	| 'agilon'
	| 'inferred'
	| 'manually_from_document'
	| 'manually_added';

interface DocumentMetadata {
	readonly name: string;
	readonly id: string;
	readonly date: string | null;
	readonly searchTerm?: string;
	readonly searchTermOccurrence?: number;
}

interface DiagnosisSourceRow {
	readonly key: string;
	readonly value: string | null;
	readonly contentType?: 'text/plain' | 'text/html' | 'text/markdown';
	readonly label?: string;
	readonly document?: DocumentMetadata | null;
}

type DiagnosisSourceTable = ReadonlyArray<{
	readonly title: string;
	readonly rows: ReadonlyArray<DiagnosisSourceRow>;
	readonly date?: string | null;
}>;

export interface DiagnosisSources {
	readonly sourceName: string;
	readonly origin?: string;
	readonly date?: string | null;
	readonly type: DiagnosisSourceType;
	readonly document?: DocumentMetadata;
	readonly data: DiagnosisSourceTable;
	readonly extraData?: ReadonlyArray<DiagnosisSourceRow>;
	readonly disclaimersMetadata?: ReadonlyArray<DiagnosisDisclaimer> | null;
}

export type ConfidenceLabel =
	| 'Recapture'
	| 'Suspect'
	| 'Suspect: Condition documented elsewhere'
	| 'Suspect: Meets diagnostic criteria'
	| 'Suspect: Clinical correlation needed'
	| null;

export type ConfidenceLabelHoverText =
	| 'Condition previously documented in the EHR'
	| 'Requires clinical validation'
	| 'Condition is not documented in the problem list or previous encounters in this EHR'
	| 'See Sources for more information'
	| 'Condition suspected, clinical correlation required'
	| '';

// TODO - this is not a type, move somewhere else
export const InAppExplanationIconTexts = {
	ReaddressDiagnosesInAppExplanation: 'Diagnoses that have been suggested by the coder to be readdressed',
	NewHCCInAppExplanation: 'Diagnoses from HCC groups that have not yet been addressed this year',
	NewHCCMidYearInAppExplanation: 'Diagnoses from HCC groups that have not yet been addressed this year',
	ExistingHCCInAppExplanation: 'Diagnoses from HCC groups that have been addressed this year',
	ExistingHCCMidYearInAppExplanation: 'Diagnoses from HCC groups that have been addressed this year',
	ProviderRejectedInAppExplanation: 'Diagnoses that had been previously rejected by the provider',
	CoderRejectedInAppExplanation: 'Diagnoses that had been previously rejected by a coder',
	AgilonNotesInAppExplanation: 'Clinical and care recommendations for the provider to agree or disagree with',
	NoHCCInAppExplanation: 'Non-HCC Diagnosis suggestions',
	CartProviderInAppExplanation:
		'All the selected diagnoses for this encounter. Clicking Add to Encounter will send to the EMR',
	CartCoderInAppExplanation:
		'All the selected diagnoses for this encounter. Clicking "Finish Scrub" will send the diagnosis suggestions to the provider',
};

export interface DiagnosisActionStatus {
	icdCode: SuggestedDiagnosis['icdCode'];
	initialStatus: DiagnosisStatus;
	currentStatus: DiagnosisStatus;
	initialDiagnosisAction: SuggestedDiagnosis['diagnosisAction'];
	currentDiagnosisAction: SuggestedDiagnosis['diagnosisAction'];
	isBackgroundAction: boolean;
	isOnlineSyncAction: boolean;
}

export type DiagnosisOrigin = SuggestionOrigin;

export enum DiagnosisDisclaimerType {
	Mismatch = 'mismatch',
	Observation = 'observation',
	SuggestionNote = 'suggestion_note',
}

export interface DiagnosisDisclaimer {
	readonly disclaimer: string;
	readonly type: DiagnosisDisclaimerType;
}

export interface SuggestedDiagnosis {
	readonly dxId: string;
	readonly icdCode: string;
	readonly icdDescription: string;
	readonly dxDescription?: string;
	readonly clientDiagnosisCodes?: ReadonlyArray<ClientDiagnosisCode>;
	readonly hccs: HCCs;
	readonly snomedCode: string;
	readonly snomedDescription: string;
	readonly sourceData?: SourceData;
	readonly source?: string;
	diagnosisSources: Array<DiagnosisSources>; // TODO - make readonly AND ReadonlyArray
	readonly suggestionDateTime: string;
	readonly billable: boolean;
	readonly billableDescendants: Array<BillableDescendant>; // TODO - make ReadonlyArray
	readonly diagnosisOptionalIcds?: Array<OptionalIcd>; // TODO - make ReadonlyArray
	readonly chosenOptionalIcd?: OptionalIcd;
	readonly isOptionalIcd: boolean;
	readonly isChosenOptionalIcd: boolean;
	readonly isBillableSubDx: boolean;
	readonly addressedGroup: DiagnosisAddressedGroup | null;
	noteData: NoteData | null; // TODO - make readonly
	readonly addedInPostVisit: boolean;
	readonly type: SourceType;
	readonly isSyncedFromEmr: boolean;
	readonly showInCart: boolean;
	readonly showInInsights: boolean;
	readonly isValid: boolean;
	readonly overrideFilter: string | null;
	readonly invalidOverride: boolean;
	readonly actionsHistory: ReadonlyArray<any>;
	status: DiagnosisStatus | null; // TODO - make readonly
	readonly statusUpdatedAt: string | null;
	readonly statusUpdatedBy: string | null;
	readonly actionUsername: string;
	readonly statusUpdatedTimestamp: number;
	/** In Unix timestamp */
	readonly statusAddedTimestamp: number;
	readonly captionInfo?: string | null;
	readonly lastCoderStatus: DiagnosisStatus | null;
	readonly lastCoderStatusUpdatedAt: string | null;
	readonly lastProviderStatus: DiagnosisStatus | null;
	readonly lastProviderStatusUpdatedAt: string | null;
	readonly isNavinaCoupledSuggestion: boolean;
	isCoupledSuggestion: boolean;
	companionOrder: number;
	isPrimary: boolean; // TODO - make readonly
	primaryDx: string | null;
	companionDxs: Array<string> | null; // TODO - make ReadonlyArray
	companionAddressedGroup: DiagnosisAddressedGroup | null;
	readonly isCoupledSuggestionCart: boolean;
	readonly isPrimaryCart: boolean | null;
	readonly companionOrderCart: number | null;
	readonly companionDxsCart: Array<string> | null; // TODO - make ReadonlyArray
	readonly primaryDxCart: string | null;
	readonly companionAddressedGroupCart: DiagnosisAddressedGroup | null;
	readonly numberOfSources: number;
	// Remodel properties
	suggestionId: string;
	suggestionUid: string;
	diagnosisUid: string;
	nid: string;
	view?: UserView;
	dateOfService: string;
	isNewDiagnosis: boolean;
	newDiagnosisStateDetails?: Pick<
		Diagnosis,
		| 'status'
		| 'actionUsername'
		| 'actionTimestamp'
		| 'diagnosisAction'
		| 'lastCoderStatus'
		| 'lastProviderStatus'
		| 'isCoderStatusFromCurrentSid'
		| 'isProviderStatusFromCurrentSid'
		| 'chosenOptionalIcd'
		| 'isOptionalIcd'
		| 'syncMethod'
	>;

	// Carry forward types
	readonly isManualFromPreviousAppointments?: boolean;
	readonly isFromRescheduledAppointment?: boolean;
	readonly rescheduledAppointmentStartTimeUnix?: number;
	readonly lastProviderStatusUpdatedByName: string | null;
	readonly lastCoderStatusUpdatedByName: string | null;

	readonly isCoderStatusFromCurrentSid?: boolean;
	readonly isProviderStatusFromCurrentSid?: boolean;

	readonly diagnosisAction: DiagnosisAction | 'NO_ACTION';

	readonly greyedOutReason?: string;
	readonly companionId: string | null;
	readonly confidenceLabel: ConfidenceLabel;
	readonly diagnosisOrigin: DiagnosisOrigin;
	readonly isHint: boolean;
	readonly shouldGenerateDocumentation: boolean;
	readonly conditionDuration: ConditionDuration;

	//group temp addition
	group?: GroupRiskModel | null;
}

interface BillableDescendant {
	readonly billable: boolean;
	readonly icdCode: string;
	readonly icdDescription: string;
	readonly snomedCode: string;
	readonly hccs: HCCs;
}

export interface OptionalIcd {
	readonly icdCode: string;
	readonly icdDescription: string;
	readonly hccs: HCCs;
	readonly hasIcd?: boolean;
	readonly snomedCode: string;
	readonly addressedGroup?: DiagnosisAddressedGroup;
	readonly clientDiagnosisCodes?: ReadonlyArray<ClientDiagnosisCode>;
}

interface WithThreshHold {
	readonly threshold: number;
}

interface MissingDiagnosesType {
	// This is the old structure of pmd, not supported anymore (25/02/21, Ron)
	// encounters: EncountersMissingDiagnosesType[];
	// observations: ObservationsMissingDiagnosesType[];
	readonly previousYears: ReadonlyArray<RafGapType>;
	readonly encountersConfig: WithThreshHold;
	readonly consultsConfig: WithThreshHold;
	readonly consolidatedPmd: { readonly [snomedCode: string]: ConsolidatedMissingDiagnosesType };
	readonly hccRecommendations: ReadonlyArray<SuggestedDiagnoses>;
}

export interface RafDiagnosis {
	readonly icdCode?: string;
	readonly description: string;
	readonly hccs: HCCs;
	readonly date?: string;
	readonly snomedCode?: string;
	hccGroup?: string; // TODO - make readonly
	readonly hccWeight?: number;
	readonly billable: boolean;
}

export type DiagnosisAddressedGroup =
	| 'NewHCCGroup'
	| 'AddressedHCCGroup'
	| 'ReAddressed'
	| 'RejectedByProvider'
	| 'RejectedByCoder'
	| 'NoHcc'
	| 'none';

export interface DemographicsType {
	readonly patientEmrId: string; // ✅ camelCase only
	readonly dataSource: number; // ✅ camelCase only
	readonly multiplePatientIds: ReadonlyArray<string>; // camelCase + array should be an array of strings
	readonly age: number; // ✅
	readonly martialStatus: string | null; // ✅ camelCase only
	readonly occupation: string | null; // ✅
	readonly sex: string; // ✅
	readonly firstName: string; // ✅ camelCase only
	readonly lastName: string; // ✅ camelCase only
	readonly dateOfBirth: string; // ✅ camelCase only
	readonly race: string; // ✅
	readonly insurances?: string; // ✅
	readonly insuranceType: string | null; // ✅ camelCase only
}

export interface LabDataType {
	readonly value: string;
	readonly originalValue: string;
	readonly low: number;
	readonly high: number;
	readonly rangeType: string;
	readonly date: string;
	readonly units: string;
	readonly isNormal: boolean;
	readonly producerId?: string;
	readonly source?: LabSource;
	readonly sourceDetails?: string;
	readonly history?: ReadonlyArray<TrendLab>;
}

export interface Observation {
	readonly high: number | null;
	readonly low: number | null;
	readonly originalValue: string;
	readonly value: number;
	readonly date: Date;
	readonly units: null | string;
	readonly producerId: null;
	readonly rangeType: string | null;
	readonly refRangeSource: string | null;
	readonly isNormal?: boolean;
	readonly observationNote?: null;
}

export enum LabDataKind {
	Collection = 'Collection',
	Single = 'Single',
	Comment = 'Comment',
	Document = 'Document',
}

export function isCollectionKindLabResult(
	navinaLabResult: NavinaLabResult,
): navinaLabResult is NavinaLabResult & { data: Record<string, LabDataType> } {
	return navinaLabResult.type === LabDataKind.Collection;
}

export function isSingleKindLabResult(
	navinaLabResult: NavinaLabResult,
): navinaLabResult is NavinaLabResult & { data: LabDataType } {
	return navinaLabResult.type === LabDataKind.Single;
}

export function isCommentLabResult(
	navinaLabResult: NavinaLabResult,
): navinaLabResult is NavinaLabResult & { data: 'normal' } {
	return navinaLabResult.type === LabDataKind.Comment;
}

export interface Preventive<TPreventiveData = Readonly<Record<string, unknown>>> {
	readonly type: string;
	readonly data: TPreventiveData;
}

export interface NavinaLabResult {
	readonly data: Record<string, LabDataType> | 'normal' | LabDataType;
	readonly date?: string;
	readonly name: string;
	readonly type: LabDataKind;
	readonly child: NavinaLabResult;
	readonly docName?: string;
	readonly docId?: string;
	readonly displayName?: string;
	readonly source?: LabSource;
	readonly sourceDetails?: string;
	readonly originalValue?: number;
	readonly observations?: { readonly [key: string]: Observation };
}

export enum LabSource {
	HIE = 'HIE',
}

export enum DocSourceType {
	HIE = 'HIE',
	EHR = 'EHR',
}

interface TrendLab {
	readonly date: string;
	readonly observationValue: null | string;
	readonly originalValue: null | string;
	readonly observationNote: null | string;
	readonly source: LabSource;
	readonly sourceDetails: null | string;
}

export interface TrendViewLab {
	readonly annotationLineGroups: ReadonlyArray<AnnotationLineGroup>;
	readonly label: string;
	readonly yAxisMin?: number;
	readonly yAxisMax?: number;
	readonly maxThresholdLine?: number;
	readonly minThresholdLine?: number;
	readonly units: string;
	readonly history: ReadonlyArray<TrendLab>;
}

export interface ImmunizationReccomendation {
	readonly name: string;
	readonly reason: string;
}

interface MedicalAlert {
	readonly name: string;
	readonly text: string;
	readonly reason: string;
}

interface StatusProps {
	readonly crawlerStatus?: boolean;
	readonly etlStatus?: boolean;
	readonly automatedSummaryStatus?: boolean;
	readonly modified?: Date;
}

export interface DisplayDoc {
	readonly name: string;
	readonly primaryName?: string;
	readonly secondaryName?: string; // TODO - make readonly
	readonly predictionCodes: ReadonlyArray<string>;
	readonly date: string;
	readonly emrInsertionDate: string;
	readonly clinicalDocDate: string;
	readonly id: string;
	readonly loinc?: string;
	readonly classifiedLabel?: string;
	readonly classifiedLoinc?: string;
	readonly classifierTags?: ReadonlyArray<string>;
	readonly classifierName?: string;
	readonly codeOrigin?: string;
	readonly icdsData?: ReadonlyArray<DocIcdData>; // TODO - make readonly
	readonly source: DocSource;
	readonly textHighlight?: ReadonlyArray<string>; // TODO - make readonly
	readonly primaryNameHighlight?: string; // TODO - make readonly
	readonly secondaryNameHighlight?: string; // TODO - make readonly
	readonly icdDescriptionHighlights?: Array<string>; // TODO - make readonly AND ReadonlyArray
	readonly icdCodeHighlights?: Array<string>; // TODO - make readonly AND ReadonlyArray
	readonly impression?: string;
	readonly hash?: string;
	readonly fileUrlPayload?: FileUrlPayload;
}

export interface DocIcdData {
	readonly description: string;
	readonly icd10Code: string;
	readonly icd10CodeLineContent: string;
	readonly icd10CodeLine: number;
	readonly snomed?: string;
	hccs: HCCs; // TODO - make readonly
	readonly hccWeight: number;
	readonly hccGroup: string;
	readonly docId: string;
	billable: boolean;
}

export interface DocSource {
	readonly name: string;
	readonly type: string;
}

export interface DiagnosisOverviewHieSource {
	readonly fileName: string;
	readonly source: DocSource;
}

export interface DocsByDISystemGroup {
	readonly [type: string]: Array<DisplayDoc>; // TODO - make ReadonlyArray
}

export interface Medication {
	readonly name: string;
	readonly dosage: string;
	readonly frequency: string;
	readonly medicationDate: string;
	readonly daysLeft: number | null;
	readonly atcLevel2?: string;
	readonly atcLevel5?: string;
	readonly displayInMedicationsList?: boolean;
	readonly rxcui?: string;
}

export interface AscvdData {
	readonly value?: number;
	readonly missingElements?: ReadonlyArray<string>;
	readonly outOfRangeElements?: ReadonlyArray<string>;
	readonly status?: 'success' | 'age_failure' | 'dx_failure' | 'other_failure';
}

export interface PreventivesType {
	readonly bmi: unknown;
	readonly bp: unknown;
	readonly breastCancerScreening: unknown;
	readonly pap: unknown;
	readonly boneDensity: unknown;
	readonly ascvdData: AscvdData;
	// Deprecated, kept for backward compatibility of old summaries
	readonly colonoscopy: {
		readonly value: { procedure?: string; pathology?: string; note?: string };
		readonly date?: any;
		readonly isFromSurgicalHistory: boolean;
		readonly source?: string;
	};
	readonly colonCancerScreening: {
		readonly value: { procedure?: string; pathology?: string; note?: string } | string;
		readonly date?: any;
		readonly isFromSurgicalHistory?: boolean;
		readonly isColonoscopy?: boolean;
		readonly source?: string;
	};
}

interface PatientData extends StatusProps {
	readonly canSeePush2Emr: boolean;
	readonly timezone: string;
	readonly clinicName: string;
	readonly allDocs: Array<DisplayDoc>; // TODO - make ReadonlyArray
	readonly icdsFromDocuments?: ReadonlyArray<DocIcdData>;
	readonly imagingAndTests: DocsByDISystemGroup;
	readonly modified: Date;
	readonly providerName: string;
	readonly emrName: ElectronicMedicalRecord;
	readonly id: string;
	readonly sid: string;
	readonly nid: string;
	readonly date: string;
	readonly startTime: string;
	readonly startTimeUnix: number;
	readonly endTimeUnix: number;
	readonly reasonForVisit: string;
	readonly allergies: ReadonlyArray<string>;
	readonly demographics: DemographicsType;
	readonly raf?: RafData;
	readonly previousEncounter: PreviousEncounter;
	readonly lastWellnessEncounter?: PreviousEncounter;
	readonly socialHistory: {
		readonly isSmoker: boolean;
		readonly isFormerSmoker: boolean;
		readonly alcoholScreening?: boolean;
		readonly numberOfChildren?: number;
	};
	readonly preventive: PreventivesType | AscvdData;
	readonly problemList: Array<ProblemType>; // TODO - make ReadonlyArray
	readonly medications: ReadonlyArray<Medication>;
	readonly immunizationsRecommendations: ReadonlyArray<ImmunizationReccomendation>;
	readonly labs: ReadonlyArray<NavinaLabResult>;
	readonly labsToShow: Array<NavinaLabResult>; // TODO - make ReadonlyArray
	readonly familyHistory: any;
	readonly surgicalHistory: ReadonlyArray<SurgicalHistoryEntry> | null;
	readonly missingDiagnoses: MissingDiagnosesType;
	readonly agilonNonDxSuggestions: ReadonlyArray<AgilonNonDxType>;
	readonly hospitalization: ReadonlyArray<NoteType>;
	readonly medicalAlerts: ReadonlyArray<MedicalAlert>;
	readonly visitType: string | null;
	readonly dataSourceUrl?: string;
	readonly dataSourceId?: number;
	readonly emrDepartmentId: string;
	readonly emrAppointmentId: string;
	readonly summaryInsightActions: ReadonlyArray<SummaryInsightAction>;
	readonly summaryInsightNotes: ReadonlyArray<SummaryInsightNote>;
	readonly summaryInsightAgilonNonDxActions: Array<AgilonNonDxStatusDataType>; // TODO - make ReadonlyArray
	readonly lastPostVisitTime: string;
	readonly lastScrubTime: string;
	readonly lastVisitScrubTime: string;
	readonly scrubberName: string;
	readonly pushToEmrId?: string;
	readonly pushContext: PushContext;
	readonly summarySuggestions: SuggestedDiagnosis[][];
	readonly nonRelevantDxs: Array<string>; // TODO - make ReadonlyArray
	readonly updates: Record<string, Update>;
	readonly riskModelConfiguration: ReadonlyArray<HccVersions>;
	readonly allowedTabs: ReadonlyArray<TabId>;
	readonly ascvdData: AscvdData | undefined;
}

export type GetAppointmentDetailsResponse = Pick<
	PatientData,
	| 'sid'
	| 'id'
	| 'nid'
	| 'date'
	| 'startTime'
	| 'providerName'
	| 'reasonForVisit'
	| 'emrAppointmentId'
	| 'startTimeUnix'
	| 'endTimeUnix'
	| 'visitType'
	| 'emrDepartmentId'
	| 'pushToEmrId'
	| 'timezone'
	| 'dataSourceId'
	| 'etlStatus'
	| 'crawlerStatus'
	| 'automatedSummaryStatus'
	| 'modified'
	| 'clinicName'
	| 'dataSourceUrl'
	| 'emrName'
	| 'problemList'
	| 'preventive'
	| 'medications'
	| 'immunizationsRecommendations'
	| 'surgicalHistory'
	| 'socialHistory'
	| 'familyHistory'
	| 'imagingAndTests'
	| 'hospitalization'
	| 'demographics'
	| 'allergies'
	| 'previousEncounter'
	| 'lastWellnessEncounter'
	| 'labs'
	| 'allDocs'
	| 'icdsFromDocuments'
	| 'labsToShow'
	| 'pushContext'
	| 'riskModelConfiguration'
	| 'allowedTabs'
	| 'ascvdData'
>;

export interface GetDxCaptureDataResponse
	extends Pick<
		PatientData,
		| 'raf'
		| 'nonRelevantDxs'
		| 'lastScrubTime'
		| 'scrubberName'
		| 'summarySuggestions'
		| 'lastVisitScrubTime'
		| 'summaryInsightAgilonNonDxActions'
		| 'agilonNonDxSuggestions'
	> {}

interface SummaryInsightNote {
	readonly id: string;
	readonly icdCode: string;
	readonly sid: string;
	readonly note: string;
	readonly updatedBy: string;
	readonly updatedByFullName: string;
	readonly role: 'provider' | 'coder';
	readonly updatedAt: string;
	readonly shouldPush: boolean;
	readonly noteDate: string;
	readonly insertionDate: string;
	readonly addedInPostVisit: boolean;
}

interface SummaryInsightAction {
	readonly id: string;
	readonly sid: string;
	readonly snomedCode: string;
	readonly icdCode: string;
	readonly metadata: PushToEmrDiagnosis;
	readonly status: DiagnosisStatus;
	readonly type: PushToEmrDiagnosisSourceType;
	readonly updatedAt: string;
	readonly updatedBy: string;
}
export interface ProblemDocType {
	readonly id: string;
	readonly loinc: string;
	readonly name: string;
	readonly primaryName: string;
	readonly secondaryName: string;
	readonly date?: string;
}

export interface NoteType {
	readonly name: string;
	readonly docName: string;
	readonly id: string;
	readonly date: string;
	readonly description?: string;
	readonly source?: string;
	readonly hash?: string;
}

export interface FileUrlPayload {
	readonly url: string;
	readonly filename: string;
}

export interface OpenSearchHit {
	readonly highlight: {
		readonly text?: ReadonlyArray<string>;
		readonly primaryName?: ReadonlyArray<string>;
		readonly secondaryName?: ReadonlyArray<string>;
		readonly icdsDataDescription?: Array<string>; // TODO - make ReadonlyArray
		readonly icdsDataIcd10Code?: Array<string>; // TODO - make ReadonlyArray
	};
	readonly id: string;
	readonly index: string;
	readonly score: number;
	readonly source: {
		readonly codeOrigin: string;
		readonly date: string;
		readonly emrFileId: string;
		readonly emrName: string;
		readonly emrPatientId: string;
		readonly icdsData: any;
		readonly id: string;
		readonly indexingDate: string;
		readonly loinc: string;
		readonly metadata: string;
		readonly name: string;
		readonly nid: string;
		readonly numOfPages: string;
		readonly predictionCodes: ReadonlyArray<string>;
		readonly primaryName: string;
		readonly secondaryName: string;
		readonly text: string;
	};
	readonly type: string;
}

export type emrNames = 'ecw' | 'athena' | 'epic' | 'veradigm' | 'none';

export interface QuicksightEmbedSettings {
	readonly dashboardNames: ReadonlyArray<DashboardNamesType>;
}

export interface EmrOnlineSyncResponse {
	readonly icd10Codes: Array<DxFromEmr>; // TODO - make ReadonlyArray
}

export enum identityProviders {
	Auth0 = 'auth0',
	Navina = 'navina',
}

export type IcdDataDB = z.infer<typeof IcdDataDBSchema>;

export interface SnomedFromIcdApiResult {
	readonly snomed: string;
}

// TODO: move it to @dxCore
export interface DiagnosisWithActionsOfUser
	extends Pick<Required<SuggestedDiagnosis>, 'suggestionUid' | 'diagnosisUid' | 'suggestionId'> {
	readonly action: Action | null;
	readonly nid: string;
	readonly isNewDiagnosis: boolean;
}

export type DiagnosisWithActionsOfUserArray = ReadonlyArray<ReadonlyArray<DiagnosisWithActionsOfUser>>;

export type SuggestedDiagnosesArray = SuggestedDiagnosis[][];

export interface LocalStorageData {
	readonly date: number;
	readonly diagnosesData: DxDict;
	readonly updatedSuggestions: SuggestedDiagnosesArray;
	readonly originalSuggestions: SuggestedDiagnosesArray;
	readonly actionsDifferences: ReadonlyArray<DiagnosisActionStatus>;
	readonly version?: number;
}

export enum DxNotificationStatus {
	Saved = 'Saved',
	UnsavedChanges = 'UnsavedChanges',
	None = 'None',
	StopShowing = 'StopShowing',
}

export type DxNotificationStatusProps = { type: NotificationType } & Pick<
	ArgsProps,
	'duration' | 'onClose' | 'message'
>;

export enum ManualDiagnosisDisabledReason {
	NonBillable = 'NonBillable',
	AlreadySelected = 'AlreadySelected',
}

export interface SummaryActionFailure {
	readonly suggestionUid?: SuggestedDiagnosis['suggestionUid'];
	readonly diagnosisUid?: SuggestedDiagnosis['diagnosisUid'];
	readonly reasonText: string;
	readonly reasonCode: number | null;
}

export interface SummaryActionsResponse {
	readonly upsertedSuggestions: SuggestedDiagnosesArray;
	readonly failures: ReadonlyArray<SummaryActionFailure>;
}

export enum UserView {
	Coder = 'coder',
	Provider = 'provider',
}

export enum ConditionDuration {
	Acute = 'Acute',
	Chronic = 'Chronic',
	TimeLimited = 'Time Limited',
}

export interface UpdateSummaryInsightsNotePayload {
	readonly nid: string;
	readonly sid: string | null;
	readonly snomed: string | null;
	readonly icdCode: string;
	readonly note: string;
	readonly noteDate: string;
	readonly shouldPush: boolean;
	readonly isPostVisit: boolean;
	readonly diagnosisUid?: string;
	readonly prevNote?: NoteData;
	readonly newNote?: NoteData | null;
	readonly dateOfService?: string;
	readonly noteId?: string;
}

export type UpdateSummaryInsightsNotePayloadProps = Pick<
	UpdateSummaryInsightsNotePayload,
	'note' | 'noteDate' | 'shouldPush' | 'noteId'
>;

export interface AmbientNoteDiagnosisContent {
	ap_title: string;
	icd10_code: string;
	icd10_description: string;
	note?: string;
	snomed_code?: string;
	hccs?: ReadonlyArray<HccVersions>;
	is_billable?: boolean;
	create_if_missing?: boolean;
	merged_ehr_text?: boolean;
}

export interface VisitCodeCalcAnswers {
	readonly [questionId: string]: number | boolean | string;
}

export interface CPTCodeContent {
	readonly cpt_code: string;
}

export interface AmbientNoteData {
	hpi_content?: string;
	pe_content?: string;
	diagnoses_content?: ReadonlyArray<AmbientNoteDiagnosisContent>;
	pi_content?: string;
	visit_code_id?: string | null;
	addon_code_ids?: ReadonlyArray<string>;
	visit_code_calc_values?: VisitCodeCalcAnswers;
	cpt_codes_content?: ReadonlyArray<CPTCodeContent>;
	replace_existing_note: boolean;
}

export interface NoteSection {
	readonly sectionType: NoteSectionType;
	readonly sectionNote: string | null;
	readonly pushed: boolean;
}

export interface HCC {
	groupId: string;
	groupName: string | undefined;
	groupWeight: string;
	riskModel: 'cms_v28' | 'cms_v24' | 'cms_rx' | 'hhs_v07' | 'kp_comm';
}

export interface NoteDiagnosis {
	readonly apNoteTitle: string;
	readonly snomedCode: string;
	readonly icd10Code: string | null;
	readonly icd10Description: string | null;
	readonly sectionNote: string | null;
	readonly hccs: ReadonlyArray<HCC>;
	readonly isBillable: boolean | null;
	readonly updatedBy: 'system' | 'user';
	readonly pushed: boolean;
	readonly missingData: string;
	readonly noteSource: string | null;
}

export interface DocumentedCode {
	readonly snomedCode: string;
	readonly icd10Code: string;
	readonly icd10Description: string;
	readonly hccs: ReadonlyArray<HCC>;
	readonly isBillable: boolean | null;
	readonly note: string | null;
	readonly source: string;
}

export interface NoteVisitCode {
	readonly codeId: string;
	readonly pushed: boolean;
}

export interface NoteAddonCode {
	readonly codeId: string;
	readonly pushed: boolean;
}

export interface NoteResponse {
	readonly noteId: string;
	readonly recordingDuration: string;
	readonly sections: ReadonlyArray<NoteSection>;
	readonly diagnoses: ReadonlyArray<NoteDiagnosis>;
	readonly documentedCodes: ReadonlyArray<DocumentedCode>;
	readonly visitCode?: NoteVisitCode | null;
	readonly addonCodeIds?: ReadonlyArray<NoteAddonCode>;
	readonly visitCodeCalcValues?: VisitCodeCalcAnswers;
}

export interface SaveDiagnosisCodesPayload {
	readonly icd10Code: string;
	readonly snomedCode: string;
	readonly updatedTitle: string;
}

export interface SaveDiagnosisPayload {
	readonly apNoteTitle: string;
	readonly code?: SaveDiagnosisCodesPayload;
}

export interface SaveNoteSectionPayload {
	readonly visitCodeId?: string | null;
	readonly addonCodeIds?: ReadonlyArray<string>;
	readonly sectionType?: NoteSectionType;
	readonly diagnosisContent?: SaveDiagnosisPayload;
}

export interface SaveNotePartDiagnosisCodes {
	readonly icd10_code: string;
	readonly snomed_code: string;
	readonly icd10_description: string;
}

export interface SaveNotePartDiagnosisContent {
	readonly ap_note_title: string;
	readonly note?: string;
	readonly code?: SaveNotePartDiagnosisCodes;
}

export interface SaveNotePartRequest {
	readonly sid: string;
	readonly visit_code_id?: string | null;
	readonly addon_code_ids?: ReadonlyArray<string>;
	readonly visit_code_calc_values?: VisitCodeCalcAnswers;
	readonly section_content?: string;
	readonly section_type?: NoteSectionType;
	readonly ap_section_content?: SaveNotePartDiagnosisContent;
}

export interface SaveNoteResponse {
	readonly success: boolean;
	readonly message: string;
}

export enum PushPredictionErrorTypes {
	EncounterIdMissing = 'EncounterIdMissing',
	NonPushableStatus = 'NonPushableStatus',
	MissingEHRLiveData = 'MissingEHRLiveData',
	PredictorNotImplementedForEHR = 'PredictorNotImplementedForEHR',
}

interface WillPushToEMRSucceed {
	readonly willPushSucceed: true;
	readonly errorType: null;
}

interface WillPushToEMRFail {
	readonly willPushSucceed: false;
	readonly errorType: PushPredictionErrorTypes;
}

export type WillPushSucceedResult = WillPushToEMRSucceed | WillPushToEMRFail;
export interface DxDict {
	[dxId: string]: PushToEmrDiagnosis;
}

export type SuggestionsMap = Map<SuggestedDiagnosis['suggestionId'], SuggestedDiagnosis[]>;

export interface SaveNewDiagnosesNotesArgs {
	readonly sid: SID | null;
	readonly nid: NID;
	readonly upsertedDiagnoses: ReadonlyArray<SuggestedDiagnosis>;
	readonly newDiagnoses: ReadonlyArray<IDiagnosisModel>;
}

export interface GetEpicTokenAndAppContextResponse {
	readonly summaryId?: string;
	readonly patientId?: string;
	readonly documentId?: string;
	readonly emrEncounterFhirId?: string;
}
